import { Chip, emphasize, Grid, IconButton, InputAdornment, TextField } from '@mui/material';
import { DateRangePicker } from 'app/ComponentLibrary/DateRangeNew';
import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExtensionContext } from '../ExtensionForm';
import {
  checkIfDurationIsOutOfBound,
  checkIfTimeOffExists,
  getTimeOffDayCount,
  isSameDateRange,
  isTimeOffOutOffExtensionBound,
} from '../helper';
import styledComponent from 'styled-components';
import { getDateRangeLabel } from 'app/ComponentLibrary/Filter/helper';
import AddBoxIcon from '@mui/icons-material/AddBox';
import { makeStyles, withStyles } from 'tss-react/mui';
import { RadioDropDown } from '.';
import { DecimalField } from './DecimalField';
import { Concatenate } from 'utils/string/string';
import { colors } from 'styles/styleVariables';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import DoneSharpIcon from '@mui/icons-material/DoneSharp';
import ClearSharpIcon from '@mui/icons-material/ClearSharp';
import { IExtTimeOffRequestDetail } from 'store/redux-store/placement-status/types';
import { convertToFormat } from 'app/helpers/dateHelper';
import _ from 'lodash';
import { Controller, useFormContext } from 'react-hook-form';

const useStyles = makeStyles<{ beingEdited: boolean }>()((theme, { beingEdited }) => ({
  datePicker: {
    width: 300,
  },
  label: {
    color: 'rgba(0, 0, 0, 0.6)',
    '&.Mui-focused': {
      color: 'rgba(0, 0, 0, 0.6)',
    },
  },
  timeField: {
    width: 150,
  },
  dailyCostField: {
    width: 100,
  },
  payerField: {
    width: 210,
  },
  approverField: {
    width: 130,
  },
  btnContainer: {
    padding: beingEdited ? '18px 12px 0' : 0,
    display: 'flex',
    gap: 4,
    '& > *': {
      padding: '0 !important',
      '&:hover': {
        backgroundColor: 'transparent !important',
        '& > *': {
          color: beingEdited ? colors.amnBlue : undefined,
        },
      },
    },
  },
}));

export const CustomTimeOff: React.FC<{
  selected: IExtTimeOffRequestDetail[];
  onChange: (values: IExtTimeOffRequestDetail[]) => void;
  disabled: boolean;
}> = ({ selected = [], onChange, disabled }) => {
  const { t } = useTranslation();

  const { startDateMoment, timeOffPayerOpts } = useContext(ExtensionContext);
  const [beingEdited, setBeingEdited] = useState<IExtTimeOffRequestDetail | null>(null);

  const { classes } = useStyles({ beingEdited: !!beingEdited });
  const { control, formState: { errors }, watch, setError, clearErrors } = useFormContext();
  const [maxDate] = useState(_.cloneDeep(startDateMoment).add(999, 'w').toDate());
  const [newSelection, setNewSelection] = useState<IExtTimeOffRequestDetail | null>(null);
  const [errorChips, setErrorChips] = useState<
    {
      startDate: string;
      endDate: string;
    }[]
  >([]);

  const endDate = watch('endDate');

  const setErrorMessages = (rangeAlreadyExists, isOutOfBound, isOutOfBoundDays) => {
    if (rangeAlreadyExists || isOutOfBound || isOutOfBoundDays)
      setError('timeOffRequest.timeOffRequestDetails', {
        message: rangeAlreadyExists
          ? t('placement.profile.requirementsAndQualifications.timeOffRequest.helperText')
          : isOutOfBound
          ? t('Outside Extension Dates')
          : isOutOfBoundDays
          ? t('Time Off Exceeds 23 Day Limit')
          : undefined,
        type: 'min',
      });
    else clearErrors('timeOffRequest.timeOffRequestDetails');
  };

  const onChoosingTimeOff = (dateRange: { startDate: string; endDate: string }) => {
    if (!dateRange.startDate && !dateRange.endDate) return;

    dateRange.endDate = convertToFormat(dateRange.endDate, 'yyyy-MM-DDTHH:mm:ss', false);
    dateRange.startDate = convertToFormat(dateRange.startDate, 'yyyy-MM-DDTHH:mm:ss', false);

    const rangeAlreadyExists = checkIfTimeOffExists(selected, dateRange);

    const newSelections = rangeAlreadyExists ? selected : [...selected, dateRange];
    const { outOfBoundDays, isOutOfBoundDays } = checkIfDurationIsOutOfBound(newSelections, 23);
    const { outOfBound, isOutOfBound } = isTimeOffOutOffExtensionBound(newSelections, startDateMoment, endDate);
    setErrorChips([...outOfBound, ...outOfBoundDays]);

    //To Avoid ranges exist error message when editing a chip and user selects same dates
    const rangeExitsHard =
      rangeAlreadyExists && (!beingEdited || newSelection.placementTimeOffId !== beingEdited.placementTimeOffId);

    setErrorMessages(rangeExitsHard, isOutOfBound, isOutOfBoundDays);
    setNewSelection(prevState => ({
      ...prevState,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      days: getTimeOffDayCount({ ...dateRange }),
      placementTimeOffPayerId: determinePayerValue(null, null),
    }));
  };

  const handleOnChange = () => {
    clearErrors('timeOffRequest.timeOffRequestDetails');
  };
  const determinePayerValue = (key: null | keyof IExtTimeOffRequestDetail, newValue) =>
    key !== 'placementTimeOffPayerId' && !newSelection?.placementTimeOffPayerId
      ? 1
      : key === 'placementTimeOffPayerId'
      ? newValue === newSelection?.placementTimeOffPayerId
        ? null
        : newValue
      : newSelection?.placementTimeOffPayerId;

  const handleFieldChanges = (key: keyof IExtTimeOffRequestDetail, newValue) =>
    setNewSelection(prevState => ({
      ...prevState,
      [key]: newValue,
      placementTimeOffPayerId: determinePayerValue(key, newValue),
    }));

  const addDateRange = () => {
    if (!!errors.timeOffRequest && !hasMinEntryError) return;
    const currentSelections = !!beingEdited
      ? selected.reduce(
          (resultArray, item) =>
            item.placementTimeOffId === beingEdited.placementTimeOffId
              ? [
                  ...resultArray,
                  { ...newSelection, placementTimeOffId: beingEdited.placementTimeOffId, isEdited: true },
                ]
              : [...resultArray, item],
          [],
        )
      : [...selected, { ...newSelection, placementTimeOffId: -Math.floor(Math.random() * 10000) }];
    onChange(currentSelections);
    clearFields();
  };

  const clearFields = () => {
    setNewSelection(null);
    setBeingEdited(null);
    clearErrors('timeOffRequest.timeOffRequestDetails');
  };

  React.useEffect(() => {
    const { outOfBound, isOutOfBound } = isTimeOffOutOffExtensionBound(selected, startDateMoment, endDate);
    setErrorChips(outOfBound);
    setErrorMessages(false, isOutOfBound, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endDate]);

  const hasMinEntryError = React.useMemo(
    () =>
      errors.timeOffRequest?.timeOffRequestDetails?.type === 'min' &&
      /** TODO @Rijash Remove message comparison */
      errors.timeOffRequest?.timeOffRequestDetails?.message === 'Add at least one timeoff',
    [errors.timeOffRequest],
  );

  const chips = useMemo(
    () =>
      selected.map((timeEntry, index) => {
        return (
          <CustomChip
            key={index}
            variant="outlined"
            label={Concatenate(
              [getDateRangeLabel({ ...timeEntry }), `(${timeEntry.days} ${timeEntry.days > 1 ? 'days' : 'day'})`],
              ' ',
            )}
            onDelete={
              timeEntry.isApproved
                ? null
                : () => {
                    const data = selected.filter(item => item !== timeEntry);
                    /** Reset error message if chip is deleted when same date range is about to be added from picker */
                    setErrorMessages(checkIfTimeOffExists(data, timeEntry), false, false);
                    setNewSelection(prevData =>
                      prevData?.placementTimeOffId === timeEntry.placementTimeOffId ? null : prevData,
                    );
                    setBeingEdited(prevData =>
                      prevData?.placementTimeOffId === timeEntry.placementTimeOffId ? null : prevData,
                    );
                    onChange(data);
                  }
            }
            onClick={() => {
              if (!beingEdited) {
                setBeingEdited({ ...timeEntry });
                setNewSelection({ ...timeEntry });
              } else {
                setBeingEdited(null);
                setNewSelection(null);
              }
            }}
            style={{
              borderColor: errorChips.some(item => isSameDateRange(timeEntry, item)) ? 'red' : undefined,
            }}
            deleteIcon={<HighlightOffIcon />}
            isSelected={!!beingEdited && _.isEqual(timeEntry, beingEdited)}
            disabled={disabled || timeEntry.isApproved}
          />
          // eslint-disable-next-line react-hooks/exhaustive-deps
        );
      }),
    [beingEdited, selected, errorChips],
  );

  const disableAddBtn = React.useMemo(
    () => !newSelection?.startDate || !newSelection?.hours || (!!errors.timeOffRequest && !hasMinEntryError),
    [errors.timeOffRequest, hasMinEntryError, newSelection?.hours, newSelection?.startDate],
  );

  const BtnContainer = React.useMemo(
    () =>
      !beingEdited ? (
        <IconButton
          type="button"
          disabled={disableAddBtn}
          disableTouchRipple
          disableFocusRipple
          onClick={() => addDateRange()}
          aria-label="skill-add-button"
          color="secondary"
          size="medium"
        >
          <AddBoxIcon fontSize="large" />
        </IconButton>
      ) : (
        <>
          <IconButton
            color="primary"
            type="button"
            onClick={() => addDateRange()}
            disableTouchRipple
            disableFocusRipple
            disabled={disableAddBtn}
          >
            <DoneSharpIcon />
          </IconButton>
          <IconButton
            color="primary"
            size="small"
            type="button"
            onClick={clearFields}
            disableTouchRipple
            disableFocusRipple
          >
            <ClearSharpIcon />
          </IconButton>
        </>
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addDateRange, disableAddBtn, beingEdited],
  );

  return (
    <TimeOffContainer container direction="column" spacing={2}>
      <Grid container item spacing={2}>
        <Grid item>
          <Controller
            control={control}
            name={'Requested Time Off *'}
            render={({ field: { onChange, ...rest } }) => (
              <DateRangePicker
                variant="filled"
                textFieldSize="medium"
                quickSelect={false}
                disablePortal={false}
                actionBar={true}
                // @ts-ignore
                onApply={onChoosingTimeOff}
                // @ts-ignore
                initialDateRange={newSelection}
                error={!!errors.timeOffRequest}
                helperText={errors.timeOffRequest ? errors.timeOffRequest.timeOffRequestDetails?.message : undefined}
                onChange={() => handleOnChange()}
                dualValidation
                trailingIconShow
                placeholder="Requested Time Off *"
                maxDate={maxDate}
                clickAway
                isDisabled={disabled}
                className={classes.datePicker}
                labelClass={classes.label}
                useMaxWidth
                maxWidth={300}
              />
            )}
          />
        </Grid>
        <Grid item>
          <DecimalField
            label="Time *"
            /** Field coleasced with empty string to clear field on deselection */
            value={newSelection?.hours ?? ''}
            onChange={newValue => handleFieldChanges('hours', newValue)}
            classes={{ root: classes.timeField }}
            InputProps={{
              endAdornment: <InputAdornment position="start">{'hours'}</InputAdornment>,
            }}
            disabled={disabled}
            error={hasMinEntryError}
          />
        </Grid>
      </Grid>
      <Grid container item spacing={2} wrap="nowrap">
        <Grid item>
          <DecimalField
            label="Daily Cost"
            /** Field coleasced with empty string to clear field on deselection */
            value={newSelection?.dailyCost ?? ''}
            onChange={newValue => handleFieldChanges('dailyCost', newValue)}
            classes={{ root: classes.dailyCostField }}
            InputProps={{
              startAdornment: <InputAdornment position="start">{t('currency.dollar')}</InputAdornment>,
            }}
            disabled={disabled}
          />
        </Grid>
        <Grid item>
          <RadioDropDown
            value={newSelection?.placementTimeOffPayerId ?? null}
            onChange={({ target: { value: newValue } }) => handleFieldChanges('placementTimeOffPayerId', newValue)}
            title={t('Payer')}
            options={timeOffPayerOpts}
            overrideClasses={{ container: classes.payerField }}
            disabled={disabled}
          />
        </Grid>
        <Grid item>
          <TextField
            /** Field coleasced with empty string to clear field on deselection */
            value={newSelection?.facilityApprovedBy ?? ''}
            onChange={e => handleFieldChanges('facilityApprovedBy', e.target.value)}
            variant="filled"
            label={t('Approved By')}
            classes={{ root: classes.approverField }}
            inputProps={{
              maxLength: 255,
            }}
            disabled={disabled}
          />
        </Grid>
        <Grid item classes={{ root: classes.btnContainer }}>
          {BtnContainer}
        </Grid>
      </Grid>
      <Grid container item>
        {chips}
      </Grid>
    </TimeOffContainer>
  );
};

const TimeOffContainer = styledComponent(Grid)`
  padding-left: 32px;
`;

const BlueChip = withStyles(Chip, theme => {
  const backgroundColor = emphasize(colors.amnLightBlue);
  const deleteIconColor = '#fff';
  return {
    root: {
      color: deleteIconColor,
      backgroundColor,
      margin: '6px 6px 0 0',
    },
    clickable: {
      cursor: 'pointer',
      '&.MuiChip-clickable.MuiChip-outlined:hover, &:hover, &:focus': {
        backgroundColor: emphasize(backgroundColor, 0.2),
      },
      '&:active': {
        backgroundColor: emphasize(backgroundColor, 0.12),
      },
    },
    deletable: {
      '&:focus': {
        backgroundColor: emphasize(backgroundColor, 0.08),
      },
    },
    deleteIcon: {
      color: deleteIconColor,
      '&:hover': {
        color: deleteIconColor,
      },
    },
  };
});

const NormalChip = withStyles(Chip, theme => ({
  root: { margin: '6px 6px 0 0' },
}));

const CustomChip = ({ isSelected, ...props }) => (isSelected ? <BlueChip {...props} /> : <NormalChip {...props} />);
